import { useContext, useEffect, useRef, useState, type CSSProperties } from "react";
import { cn } from "@/lib/utils";
import { SiteMotionContext } from "@/components/site/SiteMotionContext";

const MAX_STAGGER = 8;
const STAGGER_MS = 75;

export function useCardMotion(animateIndex = 0) {
  const { cardAnimationsEnabled } = useContext(SiteMotionContext);
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(!cardAnimationsEnabled);

  useEffect(() => {
    if (!cardAnimationsEnabled) {
      setVisible(true);
      return;
    }

    setVisible(false);
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.08, rootMargin: "0px 0px -4% 0px" },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [cardAnimationsEnabled]);

  const delay = `${Math.min(animateIndex, MAX_STAGGER) * STAGGER_MS}ms`;

  const className = cardAnimationsEnabled
    ? cn(
        "motion-safe:will-change-[transform,opacity]",
        visible
          ? "motion-safe:translate-y-0 motion-safe:opacity-100"
          : "motion-safe:translate-y-6 motion-safe:opacity-0",
        "motion-safe:transition-[transform,opacity] motion-safe:duration-700 motion-safe:ease-[cubic-bezier(0.22,1,0.36,1)]",
      )
    : undefined;

  const style: CSSProperties | undefined = cardAnimationsEnabled ? { transitionDelay: delay } : undefined;

  return { ref, className, style, enabled: cardAnimationsEnabled };
}
